/**
 * My Bookings — shows a customer's upcoming and past bookings.
 * Fetches from /a/proxy/bookings/my-bookings (requires logged-in customer).
 */

if (!customElements.get('my-bookings')) {
  customElements.define('my-bookings', class MyBookings extends HTMLElement {
    connectedCallback() {
      this.proxyBase = '/a/proxy/bookings';
      this.pastPage = 1;
      this.pastPerPage = 5;
      this.loadBookings();
    }

    async loadBookings() {
      this.innerHTML = '<div class="mb-empty">Loading your bookings...</div>';

      try {
        const res = await fetch(`${this.proxyBase}/my-bookings`);
        if (!res.ok) {
          if (res.status === 401) {
            this.innerHTML = '<div class="mb-empty"><p class="mb-empty-title">Sign in to view your bookings</p><p class="mb-empty-sub">Click the account icon in the header to sign in.</p></div>';
            this.placeVillageBanner();
            return;
          }
          throw new Error('Failed to load bookings');
        }

        this.data = await res.json();
        this.render();
      } catch (err) {
        console.error('My bookings error:', err);
        this.innerHTML = '<div class="mb-empty">Unable to load bookings. Please try again later.</div>';
        this.placeVillageBanner();
      }
    }

    render() {
      const { upcoming, past } = this.data;

      if (upcoming.length === 0 && past.length === 0) {
        this.innerHTML = '<div class="mb-empty"><p class="mb-empty-title">No bookings yet</p><p class="mb-empty-sub">Browse our events to make your first booking!</p></div>';
        this.placeVillageBanner();
        return;
      }

      let html = '<div class="mb-wrap">';

      // Upcoming
      if (upcoming.length > 0) {
        html += `<div class="mb-section">
          <div class="mb-section-hdr">
            <span class="mb-section-title">Upcoming</span>
            <span class="mb-section-count">${upcoming.length}</span>
          </div>
          <div class="mb-list">`;
        html += upcoming.map(b => this.renderBooking(b)).join('');
        html += '</div></div>';
      }

      // Past
      if (past.length > 0) {
        const totalPast = past.length;
        const pagedPast = past.slice(0, this.pastPage * this.pastPerPage);
        const hasMore = pagedPast.length < totalPast;

        html += `<div class="mb-section">
          <div class="mb-section-hdr">
            <span class="mb-section-title">Past</span>
            <span class="mb-section-count">${totalPast}</span>
          </div>
          <div class="mb-list mb-list--past">`;
        html += pagedPast.map(b => this.renderBooking(b, true)).join('');
        html += '</div>';

        if (hasMore) {
          html += `<button class="mb-load-more" data-action="load-more">Show more (${totalPast - pagedPast.length} remaining)</button>`;
        }

        html += '</div>';
      }

      html += '</div>';
      this.innerHTML = html;
      this.bindEvents();
      this.placeVillageBanner();
    }

    // The Village banner is rendered by Liquid after this component; when
    // there is a past-bookings section, tuck the banner between upcoming
    // and past so frequent bookers don't have to scroll a long archive
    // to see it.
    placeVillageBanner() {
      const banner = document.querySelector('.village-teaser-banner');
      if (!banner) return;
      const pastSection = this.querySelector('.mb-list--past')?.closest('.mb-section');
      if (pastSection) pastSection.parentNode.insertBefore(banner, pastSection);
      banner.hidden = false;
    }

    renderBooking(b, isPast = false) {
      const isCancelled = b.status === 'cancelled';
      return `
        <div class="mb-card${isPast ? ' mb-card--past' : ''}${isCancelled ? ' mb-card--cancelled' : ''}">
          <div class="mb-card-top">
            <div class="mb-card-info">
              <span class="mb-card-event">${b.event}</span>
              <span class="mb-card-meta">${b.variant} &middot; ${b.guest_count} guest${b.guest_count === 1 ? '' : 's'}</span>
            </div>
            ${isCancelled ? '<span class="mb-card-status">Cancelled</span>' : (!isPast ? `<a href="${b.manage_url}" class="mb-card-btn">Manage</a>` : '')}
          </div>
          <div class="mb-card-details">
            <span class="mb-card-detail"><span class="mb-card-icon">&#128197;</span>${b.date_display}</span>
            <span class="mb-card-detail"><span class="mb-card-icon">&#128336;</span>${b.time}</span>
            <span class="mb-card-detail"><span class="mb-card-icon">&#128205;</span>${b.location}</span>
          </div>
        </div>
      `;
    }

    bindEvents() {
      this.querySelectorAll('[data-action]').forEach(el => {
        el.addEventListener('click', () => {
          this.pastPage++;
          this.render();
        });
      });
    }
  });
}
